/**
 * CCTP swap service (Circle Cross-Chain Transfer Protocol, EVM ↔ EVM).
 *
 * Port of liberty-swap's trustless hook flow, restructured as pure helpers so
 * both extension surfaces (popup + mobile-web) can drive the flow from a shared
 * hook. CCTP moves USDC between chains by "burn on source, mint on
 * destination". The swap intent no longer travels through the relayer as a
 * signed off-chain message: it is packed into the `hookData` of Circle's
 * `depositForBurnWithHook`, and the LibertySwapHub on Base reads it back out of
 * the attested burn message. That means no intent signature, no `/intent`
 * round-trip, and no status polling — the burn transaction *is* the intent.
 *
 * The user-facing flow is two steps: approve the TokenMessenger, then burn.
 * Smart accounts batch both into one AccountOp.
 *
 * This module stays pure: it encodes hook payload + calldata and reads Circle's
 * fee endpoint. It never constructs providers/contracts (per the submodule's
 * no-new-provider rule).
 *
 * Reference (working impl):
 *   ~/Work/ZKX/liberty-swap/apps/liberty-swap/src/services/builder/cctp/encodeLibertyHook.ts
 *   ~/Work/ZKX/liberty-swap/apps/liberty-swap/src/services/builder/swap.cctp.builder.ts
 *   ~/Work/ZKX/liberty-swap/apps/liberty-swap/src/app/libs/hooks/swap/cctp/useSwapCCTP.evm.ts
 *   ~/Work/ZKX/liberty-swap/apps/liberty-swap/src/app/libs/apis/cctp.ts
 */
import { concat, getBytes, Interface, parseUnits, toBeHex, zeroPadValue } from 'ethers'

import { getLibertyApiBase } from './apiBase'
import { getLoadedSwapContractAddress } from './constants'
import { GaslessTokenLike, getSwapRecipientType, SwapRecipientType } from './gasless'
import { getDefaultIntegratorId } from './integrator'

// ───────────────────────────────────────────────────────────── endpoints ────

// CCTP relayer base — liberty-swap's deployed mainAxiosInstance host
// (NEXT_PUBLIC_MAIN_API_URL). The CCTP endpoints live on `apis.` (with the s),
// NOT the `api.` host the in-code default falls back to — that host 404s for
// CCTP. `apis.` also sends `Access-Control-Allow-Origin: *`, so the browser
// (mobile-web) can call it directly without a proxy.
export const getCctpApiBase = getLibertyApiBase

// Circle's fee oracle for fast transfers. Public, CORS-open.
export const CIRCLE_IRIS_API_BASE = 'https://iris-api.circle.com'

// The Circle TokenMessengerV2 the burn goes through (liberty-swap
// NEXT_PUBLIC_CCTP_MESSENGER_ADDRESS). Single address across chains; identical
// on staging and production.
export const CCTP_MESSENGER_ADDRESS = '0x28b5a0e9C621a5BadaA536219b3a228C8168cf5d'

/**
 * The LibertySwapHub on Base — mint recipient and destination caller of every
 * burn, and the contract that decodes `hookData`. liberty-swap resolves it as
 * the *current* USDC-BASE bridge router, so it tracks the live `/app/routers`
 * registry rather than a pinned address (the previously hardcoded hub went
 * stale once already). Callers must have loaded the routers first.
 */
export function getCctpHubAddress(): string {
  // Deliberately the loaded-only lookup: the hub is the burn's `mintRecipient`,
  // and the hardcoded fallback map holds a different environment's address, so
  // silently using it would burn USDC to a contract that never mints it back.
  const hub = getLoadedSwapContractAddress('USDC-BASE')
  if (!hub) {
    throw new Error('CCTP hub (USDC-BASE router) is not loaded — load /app/routers first')
  }

  return hub
}

// ─────────────────────────────────────────────────── token eligibility ────

// CCTP-enabled USDC tokens on EVM chains. Mirrors liberty-swap's
// `isUsingCCTP`/`supportedCCTPTokens`, minus USDC-SOLANA (Solana needs a non-EVM
// wallet and a different burn path).
export const CCTP_AVAILABLE_TOKEN_IDS: string[] = [
  'USDC-OPTIMISM',
  'USDC-UNICHAIN',
  'USDC-INK',
  'USDC-ARBITRUM',
  'USDC-POLYGON',
  'USDC-MONAD',
  'USDC-AVALANCHE'
]

export function isCCTPAvailable(tokenId: string): boolean {
  return CCTP_AVAILABLE_TOKEN_IDS.includes(tokenId)
}

// Circle CCTP domain ids (NOT chain ids).
// https://developers.circle.com/cctp/concepts/supported-chains-and-domains
export enum CCTPDomainId {
  Avalanche = 1,
  Optimism = 2,
  Arbitrum = 3,
  Solana = 5,
  Base = 6,
  Polygon = 7,
  Unichain = 10,
  Linea = 11,
  Codex = 12,
  World = 14,
  Monad = 15,
  Sei = 16,
  Ink = 21,
  Plume = 22,
  Edge = 28,
  Morph = 30
}

// Every burn is routed to the hub on Base, which then fans out to the real
// destination chain named in the hook payload.
export const HUB_DOMAIN = CCTPDomainId.Base

// chainId → CCTP domain. Mirrors liberty-swap getCCTPDomainId. PulseChain maps
// to Base's domain (the relayer bridges PulseChain USDC via Base). Returns
// undefined for unmapped chains — used for the fee lookup and validation.
export function getCCTPDomainId(chainId: number): CCTPDomainId | undefined {
  switch (chainId) {
    case 43114:
      return CCTPDomainId.Avalanche
    case 10:
      return CCTPDomainId.Optimism
    case 42161:
      return CCTPDomainId.Arbitrum
    case 369: // PulseChain → Base domain
    case 8453:
      return CCTPDomainId.Base
    case 137:
      return CCTPDomainId.Polygon
    case 143:
      return CCTPDomainId.Monad
    case 130:
      return CCTPDomainId.Unichain
    case 57073:
      return CCTPDomainId.Ink
    // Sei is receive-only here: it is a live CCTP V2 domain, but the wallet
    // ships no Sei network config, so it can never be a burn *source*. The
    // mapping exists so the PLS → USDC-SEI destination resolves a real domain
    // for Circle's fee lookup (the relayer performs the mint server-side).
    // Promote it to a source only together with a Sei network entry.
    case 1329:
      return CCTPDomainId.Sei
    default:
      return undefined
  }
}

// ────────────────────────────────────────────────────────── hook payload ────

/** Layout version the hub expects in byte 0 of `hookData`. */
export const HOOK_VERSION = 1

/** Fast Transfer threshold; a maxFee too low silently downgrades to slow. */
export const MIN_FINALITY_THRESHOLD = 1000

/**
 * Serialize the destination recipient per its type:
 *   EVM → 20 raw address bytes, SVM → 32-byte pubkey, CUSTOM → utf8 bytes.
 * SVM recipients are rejected here: decoding a base58 pubkey needs a Solana
 * dependency this submodule deliberately does not carry.
 */
export function encodeRecipientBytes(
  recipient: string,
  recipientType: SwapRecipientType
): Uint8Array {
  switch (recipientType) {
    case SwapRecipientType.EVM:
      return getBytes(recipient)
    case SwapRecipientType.CUSTOM:
      return new TextEncoder().encode(recipient)
    case SwapRecipientType.SVM:
      throw new Error('Solana recipients are not supported for CCTP swaps')
    default:
      throw new Error(`Unsupported recipient type: ${String(recipientType)}`)
  }
}

/**
 * Pack the Liberty swap intent exactly as LibertySwapHub reads it from the
 * attested burn's hookData — raw packed bytes at fixed offsets, NOT ABI
 * encoding:
 *   [0] version=1 | [1:33] destChainId u256 BE | [33:53] targetToken
 *   [53:85] integratorId | [85] recipientType | [86] recipientLen | [87:] recipient
 */
export function encodeLibertyHook(params: {
  destChainId: number
  /** 0x 20-byte EVM address of the token to receive. */
  targetToken: string
  /** 0x left-padded bytes32. */
  integratorId: string
  recipientType: SwapRecipientType
  recipient: string
}): string {
  const { destChainId, targetToken, integratorId, recipientType, recipient } = params
  const recipientBytes = encodeRecipientBytes(recipient, recipientType)
  if (recipientBytes.length > 255) throw new Error('Recipient encoding must fit in 255 bytes')

  return concat([
    new Uint8Array([HOOK_VERSION]),
    zeroPadValue(toBeHex(BigInt(destChainId)), 32),
    targetToken,
    integratorId,
    new Uint8Array([recipientType]),
    new Uint8Array([recipientBytes.length]),
    recipientBytes
  ])
}

// ─────────────────────────────────────────── depositForBurn calldata ────

// Circle TokenMessengerV2, hook variant (CCTP v2, with fast-transfer fee args).
const DEPOSIT_FOR_BURN_WITH_HOOK_ABI = [
  'function depositForBurnWithHook(uint256 amount, uint32 destinationDomain, bytes32 mintRecipient, address burnToken, bytes32 destinationCaller, uint256 maxFee, uint32 minFinalityThreshold, bytes hookData) external returns (uint64)'
]

export interface CCTPBurnTransaction {
  to: string
  data: string
  value: string
}

/**
 * Encode `depositForBurnWithHook`. The hub is both `mintRecipient` and
 * `destinationCaller` — only its `cctpReceive` may call `receiveMessage`, which
 * is what makes the flow trustless: the swap intent rides inside the attested
 * message instead of being relayed out of band.
 *
 * `maxFee` should come from `fetchCCTPMaxFee`; 0 falls back to a slow transfer.
 */
export function buildCCTPBurnTransaction(params: {
  inputToken: GaslessTokenLike
  outputToken: GaslessTokenLike
  amount: string
  destinationAddress: string
  maxFee: bigint
}): CCTPBurnTransaction {
  const { inputToken, outputToken, amount, destinationAddress, maxFee } = params
  const amountInUnits = parseUnits(amount, inputToken.decimals)
  const hubBytes32 = zeroPadValue(getCctpHubAddress(), 32)

  const hookData = encodeLibertyHook({
    destChainId: outputToken.chainId,
    targetToken: outputToken.address,
    integratorId: getDefaultIntegratorId(inputToken.id),
    recipientType: getSwapRecipientType(destinationAddress),
    recipient: destinationAddress
  })

  const iface = new Interface(DEPOSIT_FOR_BURN_WITH_HOOK_ABI)
  const data = iface.encodeFunctionData('depositForBurnWithHook', [
    amountInUnits,
    HUB_DOMAIN,
    hubBytes32,
    inputToken.address,
    hubBytes32,
    maxFee,
    MIN_FINALITY_THRESHOLD,
    hookData
  ])

  return { to: CCTP_MESSENGER_ADDRESS, data, value: '0' }
}

// ────────────────────────────────────────────────────────── relayer io ────

export interface CCTPFee {
  finalityThreshold: number
  minimumFee: number
  forwardFee?: { low: number; medium: number; high: number }
}

export async function getCCTPFee(sourceDomain: number, destDomain: number): Promise<CCTPFee[]> {
  try {
    const res = await fetch(
      `${CIRCLE_IRIS_API_BASE}/v2/burn/USDC/fees/${sourceDomain}/${destDomain}`,
      { headers: { Accept: 'application/json' } }
    )
    if (!res.ok) throw new Error(`Circle fee lookup failed (${res.status})`)

    return (await res.json()) as CCTPFee[]
  } catch (e) {
    console.error('[CCTP] fee lookup failed:', e)
    return []
  }
}

/**
 * Circle's minimum fast-transfer fee (in bps of the amount) plus a 20% buffer.
 * Returns 0n when the lookup fails — the burn then settles as a slow transfer
 * rather than reverting.
 */
export async function fetchCCTPMaxFee(
  sourceDomain: number,
  amountBaseUnits: bigint
): Promise<bigint> {
  const fees = await getCCTPFee(sourceDomain, HUB_DOMAIN)
  const minimumFee = fees[0]?.minimumFee
  if (minimumFee === undefined || minimumFee === null) return 0n

  const multiplier = BigInt(Math.round(minimumFee * 100))
  const base = (amountBaseUnits * multiplier) / 1_000_000n

  return (base * 120n) / 100n
}
